/*
 * Copyright (c) CIRROM 2026.
 */

import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import GppBadIcon from "@mui/icons-material/GppBad";
import GppGoodIcon from "@mui/icons-material/GppGood";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import { rampIconColor } from "./helpers/rampColor";
import {
  describeCheckedAgo,
  requestEmailVerification,
  useEmailVerification,
  VerificationTone,
} from "./helpers/emailVerification";
import classes from "./css/recordReview.module.css";

/*
 * Verifying an address, in two pieces that share one store.
 *
 * The TRIGGER sits in a row's action cluster beside the control that would
 * write the address, because that is the decision it informs. It is a verb -
 * check this address - in the row's own icon-button grammar. The VERDICT
 * lands on its own line beneath the address, in the ramp's colours and the
 * dossier's shield icons, so the deliverability facts one pane over and the
 * answer here speak the same language.
 *
 * Allegrow's mark rides the verdict, not the button. It names where the
 * answer came from, the way Lexology's and Companies House's marks label
 * sources on the account summary; a steward who has never heard of Allegrow
 * still knows what the button does.
 */

const ALLEGROW_MARK = "/logos/allegrow.png";

const VERIFY_HINT =
  "Check whether this address is deliverable. Uses an Allegrow credit unless it was checked in the last 14 days.";

export interface VerificationContext {
  /** Recorded on the verdict as "last requested for"; never changes it. */
  crmContactId?: string | null;
  integratedSystemId?: number | null;
}

export const VerifyEmailButton = (props: {
  email: string;
  context?: VerificationContext;
  disabled?: boolean;
}) => {
  const { entry, unavailable } = useEmailVerification(props.email);
  // A verdict on screen carries its own re-check; the trigger steps aside.
  if (entry?.phase === "verified") {
    return null;
  }
  const checking = entry?.phase === "checking";
  return (
    <Tooltip title={unavailable ?? VERIFY_HINT}>
      <span>
        <IconButton
          size="small"
          disabled={props.disabled || checking || Boolean(unavailable)}
          aria-label={`Verify ${props.email}`}
          aria-busy={checking || undefined}
          sx={{
            color: "text.secondary",
            "&:hover": { color: "text.primary" },
          }}
          onClick={() =>
            requestEmailVerification(props.email, {
              crmContactId: props.context?.crmContactId,
              integratedSystemId: props.context?.integratedSystemId,
            })
          }
        >
          {checking ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <MarkEmailReadOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

const MARKER: Record<
  VerificationTone,
  { Icon: typeof GppGoodIcon; band: string | null }
> = {
  good: { Icon: GppGoodIcon, band: "Outstanding" },
  caution: { Icon: GppMaybeIcon, band: "Average" },
  bad: { Icon: GppBadIcon, band: "Critical" },
  unknown: { Icon: HelpOutlineIcon, band: null },
};

export const EmailVerdict = (props: {
  email: string;
  context?: VerificationContext;
}) => {
  const { entry } = useEmailVerification(props.email);
  if (!entry) {
    return null;
  }

  if (entry.phase === "checking") {
    return (
      <div className={classes.verdictLine} role="status">
        <CircularProgress size={12} className={classes.verdictIcon} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Checking with Allegrow — this can take up to half a minute.
        </Typography>
      </div>
    );
  }

  if (entry.phase === "failed") {
    return (
      <div className={classes.verdictLine} role="status">
        <HelpOutlineIcon
          className={classes.verdictIcon}
          sx={{ color: "text.secondary" }}
        />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {entry.message}
          {entry.retryable && (
            <>
              {" "}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() =>
                  requestEmailVerification(props.email, {
                    crmContactId: props.context?.crmContactId,
                    integratedSystemId: props.context?.integratedSystemId,
                  })
                }
              >
                Retry
              </Link>
            </>
          )}
        </Typography>
      </div>
    );
  }

  const { result, verdict } = entry;
  const { Icon, band } = MARKER[verdict.tone];
  const iconColor = band ? rampIconColor(band) : "text.secondary";
  /*
   * Provenance, in one muted line: who said so, how long ago, and the two
   * facts a steward acts on (which mail host answers for the domain, and
   * whether this is a shared mailbox rather than a person's).
   */
  const mxProvider =
    result.mx_provider && !/^(other|unknown)$/i.test(result.mx_provider.trim())
      ? result.mx_provider
      : null;
  const provenance = [
    describeCheckedAgo(result.last_verified_at),
    mxProvider,
    result.is_role_account ? "role account" : null,
  ].filter(Boolean) as string[];
  const provenanceText = `Allegrow · ${provenance.join(" · ")}`;

  return (
    <div className={classes.verdict} role="status">
      <Tooltip title={verdict.why}>
        <div className={classes.verdictLine}>
          <Icon className={classes.verdictIcon} sx={{ color: iconColor }} />
          <Typography
            variant="body2"
            className={classes.cellValue}
            sx={{ fontWeight: 600 }}
          >
            {verdict.headline}
          </Typography>
        </div>
      </Tooltip>
      <div className={classes.verdictProvenance}>
        <img src={ALLEGROW_MARK} alt="" className={classes.verdictMark} />
        {/*
         * The line clamps in a 220px column, so the full text is one hover
         * away - with what "role account" means, on the verdicts that say it.
         */}
        <Tooltip
          title={
            result.is_role_account
              ? `${provenanceText}. A role account is a shared mailbox such as info@ or support@, not one person's.`
              : provenanceText
          }
        >
          <Typography
            variant="body2"
            className={classes.cellValue}
            sx={{ color: "text.secondary" }}
          >
            {provenanceText}
          </Typography>
        </Tooltip>
        {result.reused && (
          // describeChild keeps "Re-check" as the name; the title explains.
          <Tooltip
            title="Ask Allegrow again now. Spends a credit."
            describeChild
          >
            <Link
              component="button"
              type="button"
              variant="body2"
              className={classes.verdictRecheck}
              onClick={() =>
                requestEmailVerification(props.email, {
                  force: true,
                  crmContactId: props.context?.crmContactId,
                  integratedSystemId: props.context?.integratedSystemId,
                })
              }
            >
              Re-check
            </Link>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
